<script setup lang="ts">
import { computed } from 'vue';
import { IconName, IconSize, iconSizePx } from './icon.constants';

type Props = {
  /** Which glyph to render. */
  name: IconName;
  /** Rendered size. */
  size?: IconSize;
};

const props = withDefaults(defineProps<Props>(), {
  size: IconSize.MD,
});

const px = computed(() => iconSizePx[props.size]);

// Path data on a 24×24 grid. Icons inherit color via `currentColor` and
// scale down cleanly at smaller sizes. Copied from the admin icon assets.
// Entries are stroke-based by default; `{ d, filled: true }` renders the
// path as a solid fill instead (used for letterform glyphs like "JPG").
type IconPath = string | { d: string; filled: true };

const iconPaths: Record<IconName, IconPath[]> = {
  [IconName.ADD_CIRCLE]: [
    'M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z',
    'M8 12H16',
    'M12 16V8',
  ],
  [IconName.CLOSE]: ['M7.5 16L16 7.5', 'M16 16L7.5 7.5'],
  [IconName.DELETE]: [
    'M21 5.98C17.67 5.65 14.32 5.48 10.98 5.48C9 5.48 7.02 5.58 5.04 5.78L3 5.98',
    'M8.499 4.97L8.719 3.66C8.879 2.71 8.999 2 10.689 2H13.309C14.999 2 15.129 2.75 15.279 3.67L15.499 4.97',
    'M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14',
    'M10.33 16.5H13.66',
    'M9.501 12.5H14.501',
  ],
  [IconName.DOTS_VERTICAL]: [
    {
      d: 'M14 5C14 3.9 13.1 3 12 3C10.9 3 10 3.9 10 5C10 6.1 10.9 7 12 7C13.1 7 14 6.1 14 5Z',
      filled: true,
    },
    {
      d: 'M14 19C14 17.9 13.1 17 12 17C10.9 17 10 17.9 10 19C10 20.1 10.9 21 12 21C13.1 21 14 20.1 14 19Z',
      filled: true,
    },
    {
      d: 'M14 12C14 10.9 13.1 10 12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12Z',
      filled: true,
    },
  ],
  [IconName.ELLIPSIS]: [
    {
      d: 'M5 14C3.9 14 3 13.1 3 12C3 10.9 3.9 10 5 10C6.1 10 7 10.9 7 12C7 13.1 6.1 14 5 14Z',
      filled: true,
    },
    {
      d: 'M19 14C17.9 14 17 13.1 17 12C17 10.9 17.9 10 19 10C20.1 10 21 10.9 21 12C21 13.1 20.1 14 19 14Z',
      filled: true,
    },
    {
      d: 'M12 14C10.9 14 10 13.1 10 12C10 10.9 10.9 10 12 10C13.1 10 14 10.9 14 12C14 13.1 13.1 14 12 14Z',
      filled: true,
    },
  ],
  [IconName.CHEVRON_LEFT]: ['M15 6L9 12L15 18'],
  [IconName.CHEVRON_RIGHT]: ['M9 6L15 12L9 18'],
  [IconName.PLUS]: ['M12 5V19', 'M5 12H19'],
  [IconName.MINUS]: ['M5 12H19'],
  [IconName.EYE]: [
    'M15.58 11.9999C15.58 13.9799 13.98 15.5799 12 15.5799C10.02 15.5799 8.42 13.9799 8.42 11.9999C8.42 10.0199 10.02 8.41992 12 8.41992C13.98 8.41992 15.58 10.0199 15.58 11.9999Z',
    'M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.39997C18.82 5.79997 15.53 3.71997 12 3.71997C8.47 3.71997 5.18 5.79997 2.89 9.39997C1.99 10.81 1.99 13.18 2.89 14.59C5.18 18.19 8.47 20.27 12 20.27Z',
  ],
  [IconName.DOCUMENT_DOWNLOAD]: [
    'M9 11V17L11 15',
    'M9 17L7 15',
    'M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14',
    'M22 10H18C15 10 14 9 14 6V2L22 10Z',
  ],
  [IconName.FILE_PDF]: [
    'M4.75 11.25V5.75C4.75 4.64543 5.64543 3.75 6.75 3.75H14.1716C14.702 3.75 15.2107 3.96071 15.5858 4.33579L18.6642 7.41421C19.0393 7.78929 19.25 8.29799 19.25 8.82843V11.25M13.75 4.25V7.25C13.75 8.35457 14.6454 9.25 15.75 9.25H18.75',
    'M3.75 20.25V14.75H5.75C6.57843 14.75 7.25 15.4216 7.25 16.25C7.25 17.0784 6.57843 17.75 5.75 17.75H4.5',
    'M9.75 14.75V20.25H11.25C12.3333 20.25 14 19.7 14 17.5C14 15.3 12.3333 14.75 11.25 14.75H9.75Z',
    'M20.25 14.75H16.75V20.25',
    'M16.75 17.75H19.25',
  ],
  [IconName.FILE_IMAGE]: [
    'M4.75 11.25V5.75C4.75 4.64543 5.64543 3.75 6.75 3.75H14.1716C14.702 3.75 15.2107 3.96071 15.5858 4.33579L18.6642 7.41421C19.0393 7.78929 19.25 8.29799 19.25 8.82843V11.25M13.75 4.25V7.25C13.75 8.35457 14.6454 9.25 15.75 9.25H18.75',
    {
      d: 'M6.5 14C6.91421 14 7.25 14.3358 7.25 14.75V18.75C7.25 19.9926 6.24264 21 5 21H4.5C4.08579 21 3.75 20.6642 3.75 20.25C3.75 19.8358 4.08579 19.5 4.5 19.5H5C5.41421 19.5 5.75 19.1642 5.75 18.75V14.75C5.75 14.3358 6.08579 14 6.5 14Z',
      filled: true,
    },
    {
      d: 'M8.75 14.75C8.75 14.3358 9.08579 14 9.5 14H11.25C12.4926 14 13.5 15.0074 13.5 16.25C13.5 17.4926 12.4926 18.5 11.25 18.5H10.25V20.25C10.25 20.6642 9.91421 21 9.5 21C9.08579 21 8.75 20.6642 8.75 20.25V14.75ZM10.25 17H11.25C11.6642 17 12 16.6642 12 16.25C12 15.8358 11.6642 15.5 11.25 15.5H10.25V17Z',
      filled: true,
    },
    {
      d: 'M17.7537 15.524C17.5581 15.4756 17.3575 15.4641 17.2209 15.4724C16.5734 15.512 15.75 16.2463 15.75 17.5494C15.75 18.8354 16.4961 19.5275 17.3256 19.53C17.784 19.5236 18.2137 19.4123 18.5 19.2287V18.5761H18.1561C17.7419 18.5761 17.4061 18.2403 17.4061 17.8261C17.4061 17.4119 17.7419 17.0761 18.1561 17.0761H19.25C19.6642 17.0761 20 17.4119 20 17.8261V19.573C20 19.7606 19.9297 19.9414 19.803 20.0797C19.1384 20.8051 18.0966 21.0207 17.3391 21.0299L17.3299 21.03V21.03C15.4566 21.03 14.25 19.4349 14.25 17.5494C14.25 15.6788 15.4836 14.0758 17.1294 13.9752C17.4074 13.9583 17.7611 13.9806 18.114 14.0679C18.4605 14.1537 18.8732 14.3188 19.1993 14.6375C19.4955 14.9271 19.501 15.4019 19.2115 15.6981C18.9219 15.9944 18.4471 15.9998 18.1509 15.7103C18.0857 15.6466 17.9557 15.574 17.7537 15.524Z',
      filled: true,
    },
  ],
  [IconName.FILE_VIDEO]: [
    'M2.75 8.25C2.75 6.59315 4.09315 5.25 5.75 5.25H14.25C15.9069 5.25 17.25 6.59315 17.25 8.25V15.75C17.25 17.4069 15.9069 18.75 14.25 18.75H5.75C4.09315 18.75 2.75 17.4069 2.75 15.75V8.25Z',
    'M17.25 9L20.3439 7.62495C21.0051 7.33105 21.75 7.8151 21.75 8.53876V15.4612C21.75 16.1849 21.0051 16.669 20.3439 16.375L17.25 15',
  ],
  [IconName.FILE_AUDIO]: [
    'M9 18.5V6.5C9 5.4 9.55 4.84 11.02 4.5L16.02 3.28C17.72 2.88 19 3.78 19 5.5V16.5',
    'M6.5 21C7.88071 21 9 19.8807 9 18.5C9 17.1193 7.88071 16 6.5 16C5.11929 16 4 17.1193 4 18.5C4 19.8807 5.11929 21 6.5 21Z',
    'M16.5 19C17.8807 19 19 17.8807 19 16.5C19 15.1193 17.8807 14 16.5 14C15.1193 14 14 15.1193 14 16.5C14 17.8807 15.1193 19 16.5 19Z',
    'M9 9.5L19 7',
  ],
  [IconName.FILE_PRESENTATION]: [
    'M21.25 16.25V6.75C21.25 5.64543 20.3546 4.75 19.25 4.75H4.75C3.64543 4.75 2.75 5.64543 2.75 6.75V16.25C2.75 17.3546 3.64543 18.25 4.75 18.25H19.25C20.3546 18.25 21.25 17.3546 21.25 16.25Z',
    'M16 18.5L17 21.5M8 18.5L7 21.5M12 18.5V20.5',
    'M12 4.5V3',
  ],
  [IconName.FILE_DOCUMENT]: [
    'M4.75 19.25V5.75C4.75 4.64543 5.64543 3.75 6.75 3.75H14.1716C14.702 3.75 15.2107 3.96071 15.5858 4.33579L18.6642 7.41421C19.0393 7.78929 19.25 8.29799 19.25 8.82843V19.25C19.25 20.3546 18.3546 21.25 17.25 21.25H6.75C5.64543 21.25 4.75 20.3546 4.75 19.25Z',
    'M13.75 4.25V7.25C13.75 8.35457 14.6454 9.25 15.75 9.25H18.75',
  ],
  [IconName.ARROW_LEFT_CIRCLE]: [
    'M22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12Z',
    'M13.26 15.53L9.73999 12L13.26 8.47003',
  ],
  [IconName.ARROW_RIGHT_CIRCLE]: [
    'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z',
    'M10.74 15.53L14.26 12L10.74 8.46997',
  ],
  [IconName.BRIEFCASE]: [
    'M7.99983 22H15.9998C20.0198 22 20.7398 20.39 20.9498 18.43L21.6998 10.43C21.9698 7.99 21.2698 6 16.9998 6H6.99983C2.72983 6 2.02983 7.99 2.29983 10.43L3.04983 18.43C3.25983 20.39 3.97983 22 7.99983 22Z',
    'M8 6V5.2C8 3.43 8 2 11.2 2H12.8C16 2 16 3.43 16 5.2V6',
    'M14 13V14C14 14.01 14 14.01 14 14.02C14 15.11 13.99 16 12 16C10.02 16 10 15.12 10 14.03V13C10 12 10 12 11 12H13C14 12 14 12 14 13Z',
    'M21.65 11C19.34 12.68 16.7 13.68 14 14.02',
    'M2.62012 11.27C4.87012 12.81 7.41012 13.74 10.0001 14.03',
  ],
  [IconName.CARET_DOWN]: [{ d: 'M7 10L12 15L17 10H7Z', filled: true }],
  [IconName.CHECK]: ['M2 12.5L8.65882 19L22 6'],
  [IconName.CHEVRON_DOWN]: ['M6 9L12 15L18 9'],
  [IconName.CHEVRON_UP]: ['M6 15L12 9L18 15'],
  [IconName.CHEVRON_DOUBLE_DOWN]: [
    'M5.92918 7.7298L11.9992 13.7998L18.0692 7.7298',
    'M19.9981 16.7998L2.99808 16.7998',
  ],
  [IconName.CHEVRON_DOUBLE_UP]: [
    'M18.0686 16.2702L11.9986 10.2002L5.92859 16.2702',
    'M3.99969 7.2002H20.9997',
  ],
  [IconName.CLOUD]: [
    'M7.26998 13.0098C6.73998 12.7398 6.14998 12.5998 5.54998 12.5998C0.869978 12.9298 0.869978 19.7398 5.54998 20.0698H16.64C17.99 20.0798 19.29 19.5798 20.28 18.6698C23.57 15.7998 21.81 10.0298 17.48 9.47976C15.92 0.109757 2.38998 3.66976 5.59998 12.5998',
    'M15.85 9.91977C16.37 9.65977 16.94 9.51977 17.52 9.50977',
  ],
  [IconName.COPY]: [
    'M16 12.9V17.1C16 20.6 14.6 22 11.1 22H6.9C3.4 22 2 20.6 2 17.1V12.9C2 9.4 3.4 8 6.9 8H11.1C14.6 8 16 9.4 16 12.9Z',
    'M22 6.9V11.1C22 14.6 20.6 16 17.1 16H16V12.9C16 9.4 14.6 8 11.1 8H8V6.9C8 3.4 9.4 2 12.9 2H17.1C20.6 2 22 3.4 22 6.9Z',
  ],
  [IconName.DASHBOARD]: [
    {
      d: 'M15.4694 11.3023H19.2275C21.088 11.3023 21.9996 10.4744 21.9996 8.75349V4.53954C21.9996 2.82791 21.0973 2 19.2275 2H15.4694C13.6089 2 12.6973 2.82791 12.6973 4.53954V8.76279C12.6973 10.4744 13.5996 11.3023 15.4694 11.3023ZM14.093 4.53953C14.093 3.74884 14.2232 3.39535 15.4697 3.39535H19.2278C20.4743 3.39535 20.6046 3.73953 20.6046 4.53953V8.75349C20.6046 9.54419 20.4743 9.89767 19.2278 9.89767H15.4697C14.2232 9.89767 14.093 9.55349 14.093 8.75349V4.53953Z',
      filled: true,
    },
    {
      d: 'M15.4694 22H19.2275C21.088 22 21.9996 21.0883 21.9996 19.2279V15.4697C21.9996 13.6093 21.0973 12.6976 19.2275 12.6976H15.4694C13.6089 12.6976 12.6973 13.6 12.6973 15.4697V19.2279C12.6973 21.0883 13.5996 22 15.4694 22ZM14.093 15.4697C14.093 14.3721 14.372 14.093 15.4697 14.093H19.2278C20.3255 14.093 20.6046 14.3721 20.6046 15.4697V19.2279C20.6046 20.3255 20.3255 20.6046 19.2278 20.6046H15.4697C14.372 20.6046 14.093 20.3255 14.093 19.2279V15.4697Z',
      filled: true,
    },
    {
      d: 'M4.77209 11.3023H8.53023C10.3907 11.3023 11.3023 10.4744 11.3023 8.75349V4.53954C11.3023 2.82791 10.4 2 8.53023 2H4.77209C2.91163 2 2 2.82791 2 4.53954V8.76279C2 10.4744 2.90233 11.3023 4.77209 11.3023ZM3.39546 4.53953C3.39546 3.74884 3.52569 3.39535 4.7722 3.39535H8.53034C9.77685 3.39535 9.90708 3.73953 9.90708 4.53953V8.75349C9.90708 9.54419 9.77685 9.89767 8.53034 9.89767H4.7722C3.52569 9.89767 3.39546 9.55349 3.39546 8.75349V4.53953Z',
      filled: true,
    },
    {
      d: 'M4.77209 22H8.53023C10.3907 22 11.3023 21.0883 11.3023 19.2279V15.4697C11.3023 13.6093 10.4 12.6976 8.53023 12.6976H4.77209C2.91163 12.6976 2 13.6 2 15.4697V19.2279C2 21.0883 2.90233 22 4.77209 22ZM3.39546 15.4697C3.39546 14.3721 3.67453 14.093 4.7722 14.093H8.53034C9.62801 14.093 9.90708 14.3721 9.90708 15.4697V19.2279C9.90708 20.3255 9.62801 20.6046 8.53034 20.6046H4.7722C3.67453 20.6046 3.39546 20.3255 3.39546 19.2279V15.4697Z',
      filled: true,
    },
  ],
  [IconName.DOCUMENT_COPY]: [
    'M17 13.4V16.4C17 20.4 15.4 22 11.4 22H7.6C3.6 22 2 20.4 2 16.4V12.6C2 8.6 3.6 7 7.6 7H10.6',
    'M17.0016 13.4H13.8016C11.4016 13.4 10.6016 12.6 10.6016 10.2V7L17.0016 13.4Z',
    'M11.6016 2H15.6016',
    'M7 5C7 3.34 8.34 2 10 2H12.62',
    'M21.9975 8V14.19C21.9975 15.74 20.7375 17 19.1875 17',
    'M22 8H19C16.75 8 16 7.25 16 5V2L22 8Z',
  ],
  [IconName.DOWNLOAD]: [
    'M11.833 2.38489V18.0003L16.6664 13.167',
    'M11.8333 18.0001L7 13.1667',
    'M3 22H21',
  ],
  [IconName.EDIT]: [
    'M13.26 3.60022L5.04997 12.2902C4.73997 12.6202 4.43997 13.2702 4.37997 13.7202L4.00997 16.9602C3.87997 18.1302 4.71997 18.9302 5.87997 18.7302L9.09997 18.1802C9.54997 18.1002 10.18 17.7702 10.49 17.4302L18.7 8.74022C20.12 7.24022 20.76 5.53022 18.55 3.44022C16.35 1.37022 14.68 2.10022 13.26 3.60022Z',
    'M11.89 5.0498C12.32 7.8098 14.56 9.9198 17.34 10.1998',
    'M3 22H21',
  ],
  [IconName.ELEMENT_PLUS]: [
    'M22 8.27V4.23C22 2.64 21.36 2 19.77 2H15.73C14.14 2 13.5 2.64 13.5 4.23V8.27C13.5 9.86 14.14 10.5 15.73 10.5H19.77C21.36 10.5 22 9.86 22 8.27Z',
    'M10.5 8.52V3.98C10.5 2.57 9.86 2 8.27 2H4.23C2.64 2 2 2.57 2 3.98V8.51C2 9.93 2.64 10.49 4.23 10.49H8.27C9.86 10.5 10.5 9.93 10.5 8.52Z',
    'M10.5 19.77V15.73C10.5 14.14 9.86 13.5 8.27 13.5H4.23C2.64 13.5 2 14.14 2 15.73V19.77C2 21.36 2.64 22 4.23 22H8.27C9.86 22 10.5 21.36 10.5 19.77Z',
    'M14.5 17.5H20.5',
    'M17.5 20.5V14.5',
  ],
  [IconName.EXPORT]: [
    'M15.3 7.56C14.99 3.96 13.14 2.49 9.09 2.49H8.96C4.49 2.49 2.7 4.28 2.7 8.75V15.27C2.7 19.74 4.49 21.53 8.96 21.53H9.09C13.11 21.53 14.96 20.08 15.29 16.54',
    'M9.2 12H20.58',
    'M18.351 8.65L21.701 12L18.351 15.35',
  ],
  [IconName.EYE_SLASH]: [
    'M14.53 9.46992L9.47 14.5299C8.82 13.8799 8.42 12.9899 8.42 11.9999C8.42 10.0199 10.02 8.41992 12 8.41992C12.99 8.41992 13.88 8.81992 14.53 9.46992Z',
    'M17.82 5.76998C16.07 4.44998 14.07 3.72998 12 3.72998C8.47 3.72998 5.18 5.80998 2.89 9.40998C1.99 10.82 1.99 13.19 2.89 14.6C3.68 15.84 4.6 16.91 5.6 17.77',
    'M8.42 19.5299C9.56 20.0099 10.77 20.2699 12 20.2699C15.53 20.2699 18.82 18.1899 21.11 14.5899C22.01 13.1799 22.01 10.8099 21.11 9.39993C20.78 8.87993 20.42 8.38993 20.05 7.92993',
    'M15.51 12.7C15.25 14.11 14.1 15.26 12.69 15.52',
    'M9.47 14.53L2 22',
    'M22 2L14.53 9.47',
  ],
  [IconName.FILTER]: [
    'M5.40002 2.09985H18.6C19.7 2.09985 20.6 2.99985 20.6 4.09985V6.29985C20.6 7.09985 20.1 8.09985 19.6 8.59985L15.3 12.3999C14.7 12.8999 14.3 13.8999 14.3 14.6999V18.9999C14.3 19.5999 13.9 20.3999 13.4 20.6999L12 21.5999C10.7 22.3999 8.90002 21.4999 8.90002 19.8999V14.5999C8.90002 13.8999 8.50002 12.9999 8.10002 12.4999L4.30002 8.49985C3.80002 7.99985 3.40002 7.09985 3.40002 6.49985V4.19985C3.40002 2.99985 4.30002 2.09985 5.40002 2.09985Z',
    'M10.93 2.09985L6 9.99985',
  ],
  [IconName.MENU]: ['M3 7H21', 'M3 12H21', 'M3 17H21'],
  [IconName.REFRESH]: [
    'M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3313 3 18.2398 4.80989 19.796 7.5M21 4V8H17',
  ],
  [IconName.SEND]: [
    'M7.39999 6.31991L15.89 3.48991C19.7 2.21991 21.77 4.29991 20.51 8.10991L17.68 16.5999C15.78 22.3099 12.66 22.3099 10.76 16.5999L9.91999 14.0799L7.39999 13.2399C1.68999 11.3399 1.68999 8.22991 7.39999 6.31991Z',
    'M10.11 13.6501L13.69 10.0601',
  ],
  [IconName.SETTINGS]: [
    'M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z',
    'M2 12.8799V11.1199C2 10.0799 2.85 9.21994 3.9 9.21994C5.71 9.21994 6.45 7.93994 5.54 6.36994C5.02 5.46994 5.33 4.29994 6.24 3.77994L7.97 2.78994C8.76 2.31994 9.78 2.59994 10.25 3.38994L10.36 3.57994C11.26 5.14994 12.74 5.14994 13.65 3.57994L13.76 3.38994C14.23 2.59994 15.25 2.31994 16.04 2.78994L17.77 3.77994C18.68 4.29994 18.99 5.46994 18.47 6.36994C17.56 7.93994 18.3 9.21994 20.11 9.21994C21.15 9.21994 22.01 10.0699 22.01 11.1199V12.8799C22.01 13.9199 21.16 14.7799 20.11 14.7799C18.3 14.7799 17.56 16.0599 18.47 17.6299C18.99 18.5399 18.68 19.6999 17.77 20.2199L16.04 21.2099C15.25 21.6799 14.23 21.3999 13.76 20.6099L13.65 20.4199C12.75 18.8499 11.27 18.8499 10.36 20.4199L10.25 20.6099C9.78 21.3999 8.76 21.6799 7.97 21.2099L6.24 20.2199C5.33 19.6999 5.02 18.5299 5.54 17.6299C6.45 16.0599 5.71 14.7799 3.9 14.7799C2.85 14.7799 2 13.9199 2 12.8799Z',
  ],
  [IconName.LINK]: [
    'M13.5 12C13.5 15.18 10.93 17.75 7.75 17.75C4.57 17.75 2 15.18 2 12C2 8.82 4.57 6.25 7.75 6.25',
    'M10 12C10 8.69 12.69 6 16 6C19.31 6 22 8.69 22 12C22 15.31 19.31 18 16 18',
  ],
  [IconName.MAIL]: [
    'M17.001 20.5H7.001C4.001 20.5 2.001 19 2.001 15.5V8.5C2.001 5 4.001 3.5 7.001 3.5H17.001C20.001 3.5 22.001 5 22.001 8.5V15.5C22.001 19 20.001 20.5 17.001 20.5Z',
    'M16.999 9L13.869 11.5C12.839 12.32 11.149 12.32 10.119 11.5L6.999 9',
  ],
  [IconName.PEOPLE]: [
    'M9.16 10.87C9.06 10.86 8.94 10.86 8.83 10.87C6.45 10.79 4.56 8.84 4.56 6.44C4.56 3.99 6.54 2 9 2C11.45 2 13.44 3.99 13.44 6.44C13.43 8.84 11.54 10.79 9.16 10.87Z',
    'M16.41 4C18.35 4 19.91 5.57 19.91 7.5C19.91 9.39 18.41 10.93 16.54 11C16.46 10.99 16.37 10.99 16.28 11',
    'M4.16 14.56C1.74 16.18 1.74 18.82 4.16 20.43C6.91 22.27 11.42 22.27 14.17 20.43C16.59 18.81 16.59 16.17 14.17 14.56C11.43 12.73 6.92 12.73 4.16 14.56Z',
    'M18.34 20C19.06 19.85 19.74 19.56 20.3 19.13C21.86 17.96 21.86 16.03 20.3 14.86C19.75 14.44 19.08 14.16 18.37 14',
  ],
  [IconName.STAR]: [
    'M13.7299 3.51014L15.4899 7.03014C15.7299 7.52014 16.3699 7.99014 16.9099 8.08014L20.0999 8.61014C22.1399 8.95014 22.6199 10.4301 21.1499 11.8901L18.6699 14.3701C18.2499 14.7901 18.0199 15.6001 18.1499 16.1801L18.8599 19.2501C19.4199 21.6801 18.1299 22.6201 15.9799 21.3501L12.9899 19.5801C12.4499 19.2601 11.5599 19.2601 11.0099 19.5801L8.01991 21.3501C5.87991 22.6201 4.57991 21.6701 5.13991 19.2501L5.84991 16.1801C5.97991 15.6001 5.74991 14.7901 5.32991 14.3701L2.84991 11.8901C1.38991 10.4301 1.85991 8.95014 3.89991 8.61014L7.08991 8.08014C7.61991 7.99014 8.25991 7.52014 8.49991 7.03014L10.2599 3.51014C11.2199 1.60014 12.7799 1.60014 13.7299 3.51014Z',
  ],
  [IconName.PROFILE_ADD]: [
    'M19 19.5H14',
    'M16.5 22V17',
    'M12.1606 10.87C12.0606 10.86 11.9406 10.86 11.8306 10.87C9.45058 10.79 7.56058 8.84 7.56058 6.44C7.55058 3.99 9.54058 2 11.9906 2C14.4406 2 16.4306 3.99 16.4306 6.44C16.4306 8.84 14.5306 10.79 12.1606 10.87Z',
    'M11.9891 21.8099C10.1691 21.8099 8.35906 21.3499 6.97906 20.4299C4.55906 18.8099 4.55906 16.1699 6.97906 14.5599C9.72906 12.7199 14.2391 12.7199 16.9891 14.5599',
  ],
  [IconName.PROFILE_TIMER]: [
    'M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z',
    'M3.41016 22C3.41016 18.5478 6.47383 15.6844 10.5 15.1064',
    'M23 18C23 20.76 20.76 23 18 23C15.24 23 13 20.76 13 18C13 15.24 15.24 13 18 13C20.76 13 23 15.24 23 18Z',
    'M19.8545 19.5899L18.3045 18.6649C18.0345 18.5049 17.8145 18.1199 17.8145 17.8049V15.7549',
  ],
  [IconName.CHECKBOX_OUTLINE]: [
    {
      d: 'M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z',
      filled: true,
    },
  ],
  [IconName.CHECKBOX_CHECKED]: [
    {
      d: 'M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z',
      filled: true,
    },
  ],
  [IconName.CHECKBOX_INDETERMINATE]: [
    {
      d: 'M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM17 13H7V11H17V13Z',
      filled: true,
    },
  ],
  [IconName.INFO_CIRCLE]: [
    'M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z',
    'M11.998 17L11.998 12',
    'M12.0035 9L11.9945 9',
  ],
  [IconName.CHECK_CIRCLE]: [
    'M11.9199 22C17.4199 22 21.9199 17.5 21.9199 12C21.9199 6.5 17.4199 2 11.9199 2C6.41992 2 1.91992 6.5 1.91992 12C1.91992 17.5 6.41992 22 11.9199 22Z',
    'M8.5 12.5L10.8373 15L15.5 10',
  ],
  [IconName.WARNING]: [
    'M3.08107 14.6721L9.44849 4.37585C10.6211 2.47972 13.3789 2.47972 14.5515 4.37584L20.9189 14.6721C22.1549 16.6707 20.7173 19.25 18.3674 19.25H5.63258C3.28267 19.25 1.84509 16.6707 3.08107 14.6721Z',
    'M12 8.75V12.75',
    'M12 15.5V15.49',
  ],
  [IconName.ERROR_CIRCLE]: [
    'M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z',
    'M12 7V12',
    'M12.0035 15L11.9945 15',
  ],
};

const paths = computed(() =>
  iconPaths[props.name].map((path) =>
    typeof path === 'string' ? { d: path, filled: false } : path,
  ),
);
</script>

<template>
  <svg
    :width="px"
    :height="px"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    class="inline-block shrink-0"
  >
    <path
      v-for="(path, i) in paths"
      :key="i"
      :d="path.d"
      :fill="path.filled ? 'currentColor' : undefined"
      :stroke="path.filled ? 'none' : undefined"
      :fill-rule="path.filled ? 'evenodd' : undefined"
      :clip-rule="path.filled ? 'evenodd' : undefined"
    />
  </svg>
</template>
